import { Box, Button, Stack } from "@mui/material";
import { useContext, useState } from "react";
import { OrganizationContext } from "../../contexts/OrganizationContext";
import { useTranslation } from "react-i18next";
import AddAssetModal from "./AddAssetModal";
import { SubOrganizations } from "../../models/SubOrganization";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridToolbarProps,
} from "@mui/x-data-grid";
import { DROPDOWN_EMISSION_GROUP } from "../../components/emission/emission-components";
import { useAuth } from "../../contexts/UserContext";
import alertService from "../../components/alert/alertService";
import { EmissionGroup } from "../../models/EmissionGroup";
import { AppDataGrid } from "../../components/datagrid/AppDataGrid";
import { common, input } from "../../i18n";
import { CustomGridToolbarQuickFilter } from "../../components/datagrid/CustomGridToolbarQuickFilter";
import TableApprovalStatus from "../emission/_components/data-table/TableApprovalStatus";
import { tableStyle } from "../emission/_components/data-table/EmissionDataTableStyle";

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    onAdd?: () => void;
    addLabel?: string;
  }
}

const getLocation = (subOrganization?: SubOrganizations) => {
  const address = subOrganization?.location?.address ?? "";
  const district = subOrganization?.location?.district ?? "";
  const city = subOrganization?.location?.city ?? "";
  return [address, district, city].join(" ");
};

const Assets = () => {
  const context = useContext(OrganizationContext);
  const { isAdmin } = useAuth();
  const [openNew, setOpenNew] = useState<boolean>(false);
  const [rowSelect, setRowSelect] = useState<any>();
  const { t } = useTranslation([common, input], { nsMode: "fallback" });

  if (!context) {
    throw Error();
  }

  const showSubOrganization = context.selectedSubOrganization === undefined;
  const selectedSubOrganization = context.subOrganizations?.find(
    (item) => item.id === context.selectedSubOrganization,
  );

  const rows =
    context.filteredAssets?.map((item) => {
      const subOrganization = context.subOrganizations?.find(
        (sub) => sub.name === item.suborganizationName,
      );

      return {
        id: item._id,
        name: item.name,
        assetType: item.assetType,
        emissionGroup: item.emissionGroup,
        subOrganization: subOrganization,
        location: getLocation(subOrganization),
        status: item.assetStatus,
        siteName: item.siteName,
      };
    }) ?? [];

  const organizationsMenuItem = [
    {
      nameTH: context.organization?.name ?? "",
      nameEN: context.organization?.name ?? "",
      value: context.organization?._id ?? "",
    },
  ];

  const emissionGroupMenuItem = DROPDOWN_EMISSION_GROUP.map((item) => {
    return {
      nameTH: t(item.groupTh),
      nameEN: t(item.groupEn),
      value: item._id,
      disabled: false,
    };
  });

  let subOrganizationsMenuItem: {
    value: string;
    nameTH: string;
    nameEN: string;
  }[];
  if (context.subOrganizations === undefined) {
    subOrganizationsMenuItem = [];
  } else {
    if (context.selectedSubOrganization) {
      const name = selectedSubOrganization?.name ?? "";
      subOrganizationsMenuItem = [
        {
          nameTH: name,
          nameEN: name,
          value: selectedSubOrganization?.id.toString() ?? "",
        },
      ];
    } else {
      subOrganizationsMenuItem = context.subOrganizations?.map((item) => {
        const name = item.name;
        return { nameTH: name, nameEN: name, value: item.id.toString() };
      });
    }
  }

  const assetType = Array.from(
    new Set(context.assets?.map((item) => item.assetType)),
  ).map((item) => {
    return {
      nameTH: item,
      nameEN: item,
      value: item,
    };
  });

  const onClickAdd = () => {
    setOpenNew(true);
  };

  const onRowClick = (params: GridRowParams) => {
    if (!isAdmin() || params.row.status === "deactivated") return;
    setRowSelect(params.row);
    setOpenNew(true);
  };

  const onClickDelete = async (row: any) => {
    const isConfirmed = await alertService.confirm(
      <Box>{t("Delete-Asset")}</Box>,
      <Box>
        {`${t("Are-You-Sure-Want-To-Delete")} `}
        <span style={{ color: "var(--red)" }}>{row.name}</span>
        {` ${t("This-Action-Cannot-Be-Undone")} `}
      </Box>,
      t("Delete"),
      "var(--red)",
    );

    if (isConfirmed) {
      context.deleteAsset(row.id, row.subOrganization.id);
    }
  };

  const onAssetAddClick = (
    organization?: string,
    emissionGroup?: string,
    subOrganization?: string,
    assetType?: string,
    assetName?: string,
    assetStatus?: string,
    site?: string,
  ) => {
    if (
      organization === undefined ||
      emissionGroup === undefined ||
      subOrganization === undefined ||
      assetType === undefined ||
      assetName === undefined ||
      assetStatus === undefined ||
      site === undefined
    ) {
      return;
    }
    context.addAsset(
      organization,
      emissionGroup,
      subOrganization,
      assetType,
      assetName,
      assetStatus,
      site,
    );
    setOpenNew(false);
  };

  const onAssetEditClick = (
    assetType?: string,
    assetName?: string,
    assetStatus?: string,
    site?: string,
  ) => {
    if (
      assetType === undefined ||
      assetName === undefined ||
      assetStatus === undefined ||
      site === undefined ||
      rowSelect === undefined
    ) {
      return;
    }
    context.editAsset(
      rowSelect.id,
      rowSelect.subOrganization.id,
      assetType,
      assetName,
      assetStatus,
      site,
    );
    setRowSelect(undefined);
    setOpenNew(false);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("Asset-Name"),
      width: 160,
      flex: 1,
      disableColumnMenu: true,
      headerAlign: "left",
      valueFormatter: (value) => t(value),
    },
    {
      field: "assetType",
      headerName: t("Asset-Type"),
      width: 150,
      flex: 1,
      disableColumnMenu: true,
      headerAlign: "left",
      valueFormatter: (value) => t(value),
    },
    {
      field: "emissionGroup",
      headerName: t("Emission-Group"),
      width: 160,
      disableColumnMenu: true,
      headerAlign: "left",
      valueFormatter: (value) => {
        try {
          const emission: EmissionGroup | undefined =
            DROPDOWN_EMISSION_GROUP.find((e) => e.emissionGroup === value);
          return t(emission!.groupTh);
        } catch (error) {
          return value;
        }
      },
    },
    {
      field: "subOrganization",
      headerName: t("Sub-Organizations"),
      width: 150,
      disableColumnMenu: true,
      headerAlign: "left",
      valueGetter: (value: any) => value?.name || "",
    },
    {
      field: "siteName",
      headerName: t("Site"),
      width: 100,
      disableColumnMenu: true,
      headerAlign: "left",
    },
    {
      field: "status",
      headerName: t("Asset-Status"),
      width: 170,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return <TableApprovalStatus status={params.value} by={""} />;
      },
    },
    {
      field: "",
      disableColumnMenu: true,
      sortable: false,
      type: "actions",
      width: 70,
      getApplyQuickFilterFn: undefined,
      headerAlign: "left",
      getActions: (params) => {
        return isAdmin() && params.row.status !== "deactivated"
          ? [
              <GridActionsCellItem
                key={`${params.row.id}-delete`}
                icon={<img src="/img/trash.svg" alt="icon-delete" />}
                label="delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onClickDelete(params.row);
                }}
              />,
            ]
          : [];
      },
    },
  ];

  return (
    <Box
      sx={{
        height: { lg: "100%" },
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <Stack minHeight={0} height={1} spacing={"40px"}>
        <AppDataGrid
          columns={columns}
          rows={rows}
          rowHeight={48}
          onRowClick={onRowClick}
          columnVisibilityModel={{
            subOrganization: showSubOrganization,
            location: showSubOrganization,
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 100 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          sx={{
            width: "100%",
            height: "100%",
            ...tableStyle("dark"),
            ...(!isAdmin() && { "& .MuiDataGrid-row": { cursor: "default" } }),
          }}
          slots={{ toolbar: QuickSearchToolbar }}
          slotProps={{
            toolbar: isAdmin()
              ? { onAdd: onClickAdd, addLabel: t("Add-Asset") }
              : {},
          }}
        />
      </Stack>
      <AddAssetModal
        editItem={rowSelect}
        open={openNew}
        onClose={() => {
          setOpenNew(false);
          setRowSelect(undefined);
        }}
        currentOrganization={context.organization?._id}
        organizationsMenuItem={organizationsMenuItem}
        emissionGroup={emissionGroupMenuItem}
        selectedSubOrganization={selectedSubOrganization?.id.toString()}
        subOrganizationsMenuItem={subOrganizationsMenuItem}
        assetType={assetType}
        onAddClick={onAssetAddClick}
        onEditClick={onAssetEditClick}
      />
    </Box>
  );
};

export default Assets;

const QuickSearchToolbar = (
  props: GridToolbarProps & { onAdd?: () => void; addLabel?: string },
) => {
  return (
    <Box
      marginBottom={"20px"}
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
      gap={4}
      sx={{
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <CustomGridToolbarQuickFilter />
      {props.onAdd && (
        <Button
          variant="contained"
          color="secondary"
          onClick={props.onAdd}
          startIcon={<img src="/img/add-circle.svg" alt="add-circle" />}
        >
          {props.addLabel}
        </Button>
      )}
    </Box>
  );
};
